import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../../../actions/authActions';
import { useAuth } from '../../../context/AuthContext';
import participantsApi from '../../../services/api/participants/participantsApi';
import WbLoader from '../../../components/common/WbLoader';
import WbAlert from '../../../components/common/WbAlert';
import paths from '../../../routes/paths';
import { Box, Container, Typography, Button, Paper } from '@mui/material';

export default function EmailLinkVerify() {
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { dispatch: authDispatch, actions } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('No verification token provided');
      setLoading(false);
      return;
    }

    const verifyToken = async () => {
      try {
        // Decode the token properly - it comes URL encoded from email links
        const decodedToken = decodeURIComponent(token);
                
        // Call the API to verify the email token
        const response = await participantsApi.verifyEmailToken(decodedToken);
        
        if (!response) {
          throw new Error('Invalid or expired verification link');
        }

        let userData;
        
        // Parse the response if it's a string
        if (typeof response === 'string') {
          try {
            userData = JSON.parse(response);
          } catch (parseError) {
            console.error('Error parsing response:', parseError);
            throw new Error('Invalid response format');
          }
        } else {
          userData = response;
        }
        
        // Store the user data in localStorage
        localStorage.setItem('pupil', typeof response === 'string' ? response : JSON.stringify(response));

        // For Redux
        dispatch(loginSuccess({
          user: userData,
          provider: 'email'
        }));
        
        // For Context API
        authDispatch({ 
          type: actions.LOGIN_SUCCESS, 
          payload: { 
            user: userData, 
            provider: 'email' 
          } 
        });
        
        // Redirect to surveys page with replace (to prevent going back to verification)
        navigate(paths.pupilSurveys, { replace: true });
      } catch (err) {
        // Log the status explicitly. It decides which of two unrelated faults this is, and until now
        // it was discarded before anything could see it - including whoever was reading this console
        // trying to work out why a link failed.
        console.error('Email verification error (status %s):', err?.status ?? 'unknown', err);
        setErrorStatus(err?.status ?? null);
        setError(err.message || 'Failed to verify email link');
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token, navigate, dispatch, authDispatch, actions]);

  if (loading) {
    return <WbLoader />;
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            {errorStatus === 404 ? "We couldn't find your account" : 'Verification Failed'}
          </Typography>
          <Typography variant="body1" paragraph>
            {/* 400 and 404 are different faults and only one of them is the child's to act on.
                A broken or expired link can be fixed by asking for a new one. A 404 means the link
                was fine and we could not find their record - nothing they do will change that, so
                telling them to try again wastes their time and hides the problem from support. */}
            {errorStatus === 404
              ? 'Your link worked, but we could not find your account. Please tell a member of staff at your school - they can sort this out for you.'
              : errorStatus === 400
                ? 'This link is not valid any more. Please ask for a new one from your school.'
                : error}
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => navigate('/login')}
          >
            Back to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  // Return loading indicator while redirect happens
  return <WbLoader />;
}